import { useRef, useState } from "react";

import {
  PROPULSOR_DEMO_EVIDENCE,
  PROPULSOR_DEMO_CANARIES,
  PropulsorDemoController,
  type PropulsorCanaryId,
  type PropulsorRadialStation,
  type PropulsorSourceOccurrence,
} from "./propulsorDemoModel";
import "./propulsor-demo.css";

const VIEW_CENTER = 140;
const SOURCE_RADIUS = 84;

function bladePath(
  radius: number,
  hub: number,
  pitchDeg: number,
  stations: readonly PropulsorRadialStation[],
): string {
  const maxChord = Math.max(...stations.map((station) => station.chord));
  const projected = stations.map((station) => {
    const projection = Math.max(
      0.2,
      Math.abs(Math.cos((station.twistDeg + pitchDeg) * Math.PI / 180)),
    );
    return {
      x: Math.max(hub, radius * station.radiusFraction),
      halfWidth: (3 + 9 * station.chord / maxChord) * projection,
    };
  });
  const upper = projected.map((point) => `${point.x} ${-point.halfWidth}`).join(" L ");
  const lower = projected.slice().reverse()
    .map((point) => `${point.x} ${point.halfWidth}`).join(" L ");
  return `M ${hub} 0 L ${upper} L ${lower} Z`;
}

function BladeSet({
  bladeCount,
  className,
  diameterScale = 1,
  hubFraction,
  pitchDeg,
  stations,
}: {
  readonly bladeCount: number;
  readonly className: string;
  readonly diameterScale?: number;
  readonly hubFraction: number;
  readonly pitchDeg: number;
  readonly stations: readonly PropulsorRadialStation[];
}) {
  const radius = Math.max(18, Math.min(112, SOURCE_RADIUS * diameterScale));
  const hub = Math.max(5, Math.min(radius * 0.48, radius * hubFraction));
  return <g className={className} transform={`translate(${VIEW_CENTER} ${VIEW_CENTER})`}>
    {Array.from({ length: bladeCount }, (_, index) => <path
      d={bladePath(radius, hub, pitchDeg, stations)}
      key={index}
      transform={`rotate(${index * 360 / bladeCount})`}
    />)}
    <circle cx="0" cy="0" r={hub} />
    <line x1={-radius - 6} x2={radius + 6} y1="0" y2="0" />
  </g>;
}

function DiskReference({ count }: { readonly count: number }) {
  return <>
    {Array.from({ length: count }, (_, index) => {
      const column = index % 7;
      const row = Math.floor(index / 7);
      return <circle
        className="propulsor-demo__disk"
        cx={35 + column * 35}
        cy={108 + row * 64}
        key={index}
        r={index === 0 || index === count - 1 ? 15 : 11}
      />;
    })}
  </>;
}

function SourceFacts({ source }: { readonly source: PropulsorSourceOccurrence }) {
  return <dl className="propulsor-demo__facts">
    <div><dt>Source component</dt><dd>{source.sourceComponentId}</dd></div>
    <div><dt>Occurrence</dt><dd>{source.occurrenceId}</dd></div>
    <div><dt>Stable shaft axis</dt><dd>{source.axisId} · [{source.axis.join(", ")}]</dd></div>
    <div><dt>Hub</dt><dd>{source.hubKind} · {source.hubRadius.toFixed(6)} source-unit</dd></div>
    <div><dt>Units</dt><dd>{source.lengthUnit} · {source.unitAttestation}</dd></div>
    <div><dt>Rotation</dt><dd>{source.rotationSense} · {source.orientationParity}</dd></div>
    <div><dt>Radial definition</dt><dd>{source.stations.length} bounded chord / twist / profile stations</dd></div>
    <div><dt>Placement</dt><dd>{source.parentSourceComponentId
      ? `parent ${source.parentSourceComponentId} retained · transform partial`
      : "source-local root occurrence"}</dd></div>
  </dl>;
}

interface PropulsorDemoProps {
  readonly controller?: PropulsorDemoController;
  readonly taskDriven?: boolean;
}

type PropulsorInputField = "blade-count" | "collective-pitch" | "diameter";

interface PropulsorInputError {
  readonly field: PropulsorInputField;
  readonly message: string;
}

export function PropulsorDemo({
  controller: suppliedController,
  taskDriven = false,
}: PropulsorDemoProps = {}) {
  const localController = useRef<PropulsorDemoController | null>(null);
  if (localController.current === null) localController.current = new PropulsorDemoController();
  const controller = suppliedController ?? localController.current;
  const [snapshot, setSnapshot] = useState(() => controller.snapshot());
  const [inputError, setInputError] = useState<PropulsorInputError | null>(null);
  const source = snapshot.sourcePropulsor;
  const loom = snapshot.loomPropulsor;
  const regenerate = (operation: () => void) => {
    operation();
    setInputError(null);
    setSnapshot(controller.snapshot());
  };
  const setValidated = (
    field: PropulsorInputField,
    value: number,
    operation: (candidate: number) => void,
  ) => {
    try {
      if (!Number.isFinite(value)) throw new RangeError("value must be a finite number");
      regenerate(() => operation(value));
    } catch (error: unknown) {
      setInputError({
        field,
        message: error instanceof Error ? error.message : "input is outside the supported range",
      });
    }
  };
  const diameterScale = source && loom ? loom.diameter / source.diameter : 1;
  const hubFraction = source ? source.hubRadius / (source.diameter / 2) : 0.12;

  return <article
    className="propulsor-demo"
    data-definition-signature={snapshot.definitionSignature}
    data-fixture-revision={PROPULSOR_DEMO_EVIDENCE.fixtureRevision}
    data-operating-signature={snapshot.operatingSignature}
    data-preview-signature={snapshot.previewSignature}
    data-task-driven={taskDriven}
    data-testid="propulsor-demo"
  >
    <header className="propulsor-demo__header">
      <div>
        {taskDriven ? null : <span>Loom primitive · FXC-10468</span>}
        {taskDriven ? <h3>Rotor comparison</h3> : <h2>Propulsor definition lab</h2>}
        <p>{taskDriven
          ? "Adapted source comparison for this session only. Changes are not authoritative or persisted."
          : "Regenerate an editable Loom rotor over a revision-bound Airshow parameter schematic."}</p>
      </div>
      <strong data-testid="propulsor-fidelity">{snapshot.canEdit
        ? "Adapted radial-station · unit unattested"
        : "Typed partial · source disks only"}</strong>
    </header>

    <section className="propulsor-demo__body">
      <div className="propulsor-demo__visual">
        <div className="propulsor-demo__legend" aria-label="Comparison legend">
          <span data-layer="source">Amber · source parameter schematic</span>
          <span data-layer="loom">Cyan · Loom regeneration</span>
        </div>
        <svg aria-label="Propulsor parameter schematic and Loom display comparison" role="img" viewBox="0 0 280 280">
          <g data-testid="propulsor-loom-layer">
            {loom ? <BladeSet
              bladeCount={loom.bladeCount}
              className="propulsor-demo__loom-geometry"
              diameterScale={diameterScale}
              hubFraction={hubFraction}
              pitchDeg={loom.collectivePitchDeg}
              stations={loom.stations}
            /> : null}
          </g>
          <g data-testid="propulsor-reference-layer">
            {source ? <BladeSet
              bladeCount={source.bladeCount}
              className="propulsor-demo__source-geometry"
              hubFraction={hubFraction}
              pitchDeg={source.collectivePitchDeg}
              stations={source.stations}
            /> : <DiskReference count={snapshot.canary.sourceDisks.length} />}
          </g>
        </svg>
        <div className="propulsor-demo__comparison">
          <p><span>Source diameter</span><strong data-testid="propulsor-source-diameter">
            {source ? `${source.diameter.toFixed(3)} source-unit` : "disk envelope only"}
          </strong></p>
          <p><span>Loom diameter</span><strong data-testid="propulsor-loom-diameter">
            {loom ? `${loom.diameter.toFixed(3)} source-unit` : "not generated"}
          </strong></p>
          <p><span>Authored parameter delta</span><strong data-testid="propulsor-parameter-residual">
            {snapshot.parameterResidualPercent.toFixed(2)}%
          </strong></p>
          <p><span>Surface residual</span><strong data-testid="propulsor-surface-residual">
            Not attested — evaluator parity pending
          </strong></p>
          <p><span>Source render residual</span><strong>Unavailable — no source-render artifact</strong></p>
        </div>
      </div>

      <div className="propulsor-demo__controls">
        <label>Airshow canary
          <select
            onChange={(event) => regenerate(() => controller.selectCanary(
              event.target.value as PropulsorCanaryId,
            ))}
            value={snapshot.canary.id}
          >
            {PROPULSOR_DEMO_CANARIES.map((item) => <option key={item.id} value={item.id}>
              {item.label}
            </option>)}
          </select>
        </label>

        {snapshot.canary.propulsors.length > 1 ? <div className="propulsor-demo__occurrences">
          <span>Separate occurrences</span>
          {snapshot.canary.propulsors.map((item) => <button
            aria-pressed={snapshot.selectedOccurrenceId === item.occurrenceId}
            data-testid="propulsor-occurrence"
            key={item.occurrenceId}
            onClick={() => regenerate(() => controller.selectOccurrence(item.occurrenceId))}
            type="button"
          >{item.label} · {item.sourceComponentId}</button>)}
        </div> : null}

        {source ? <>
          <p className="propulsor-demo__selection" data-testid="propulsor-selected-occurrence">
            Selected {source.label} · {source.sourceComponentId}
          </p>
          <div className="propulsor-demo__inputs">
            <label>Diameter
              <input
                aria-label="Diameter"
                aria-describedby={inputError?.field === "diameter"
                  ? "propulsor-input-error" : undefined}
                aria-invalid={inputError?.field === "diameter"}
                disabled={!snapshot.canEdit}
                max="10000"
                min="0.001"
                onChange={(event) => setValidated(
                  "diameter", event.target.valueAsNumber,
                  (value) => controller.setDiameter(value),
                )}
                step="0.001"
                type="number"
                value={loom?.diameter ?? 0}
              />
              <small>{source.lengthUnit} · {source.unitAttestation} · definition</small>
            </label>
            <label>Blade count
              <input
                aria-label="Blade count"
                aria-describedby={inputError?.field === "blade-count"
                  ? "propulsor-input-error" : undefined}
                aria-invalid={inputError?.field === "blade-count"}
                disabled={!snapshot.canEdit}
                max="64"
                min="1"
                onChange={(event) => setValidated(
                  "blade-count", event.target.valueAsNumber,
                  (value) => controller.setBladeCount(value),
                )}
                step="1"
                type="number"
                value={loom?.bladeCount ?? 0}
              />
              <small>definition</small>
            </label>
            <label>Collective pitch
              <input
                aria-label="Collective pitch"
                aria-describedby={inputError?.field === "collective-pitch"
                  ? "propulsor-input-error" : undefined}
                aria-invalid={inputError?.field === "collective-pitch"}
                disabled={!snapshot.canEdit}
                max="180"
                min="-180"
                onChange={(event) => setValidated(
                  "collective-pitch", event.target.valueAsNumber,
                  (value) => controller.setCollectivePitch(value),
                )}
                step="0.1"
                type="number"
                value={loom?.collectivePitchDeg ?? 0}
              />
              <small>deg · operating state</small>
            </label>
          </div>
          {inputError ? <p
            className="propulsor-demo__input-error"
            id="propulsor-input-error"
            role="alert"
          ><strong>Input rejected.</strong> {inputError.message}. Last valid value retained.</p> : null}
        </> : <section className="propulsor-demo__blocked" data-testid="propulsor-blocked-state">
          <strong>{snapshot.issueCode}</strong>
          <p>{snapshot.canary.sourceDisks.length} source disks are visible in amber, but this VSP file has no blade definition. Loom will not invent blade count, chord, twist, profile, hub, RPM, or pitch.</p>
          <label>Diameter<input aria-label="Diameter" disabled type="number" value="0" readOnly /></label>
          <label>Blade count<input aria-label="Blade count" disabled type="number" value="0" readOnly /></label>
          <label>Collective pitch<input aria-label="Collective pitch" disabled type="number" value="0" readOnly /></label>
        </section>}

        <details className="propulsor-demo__technical" data-technical-details>
          <summary>Technical evidence</summary>
          {source ? <>
            <SourceFacts source={source} />
            <details className="propulsor-demo__stations">
              <summary>{source.stations.length} radial stations</summary>
              <table>
                <thead><tr><th>r/R</th><th>Chord</th><th>Twist</th><th>Profile</th></tr></thead>
                <tbody>{source.stations.map((row) => <tr key={row.radiusFraction}>
                  <td>{row.radiusFraction.toFixed(3)}</td>
                  <td>{row.chord.toFixed(4)}</td>
                  <td>{row.twistDeg.toFixed(2)}°</td>
                  <td>{row.profileFamily}</td>
                </tr>)}</tbody>
              </table>
            </details>
          </> : null}
          <aside className="propulsor-demo__boundary"
            data-testid="loom-build-propulsor-evidence">
            <strong>Evidence boundary</strong>
            <p>This adapted comparison is session-only. Its edits survive task changes, but are not authoritative or persisted to the accepted Loom scene or project history.</p>
            <p>Stable component, definition, occurrence, shaft-axis and rotation-sense identity survive adaptation. Diameter, blade count, hub and bounded radial sections are authored geometry; collective pitch and unbound RPM are operating state.</p>
            <p>Amber is a parameter-derived schematic, not an OpenVSP source render. The fixture is generated by the adapter and bound to the source and definition revisions below.</p>
            <p>No exact surface, solid, Boolean, source round-trip, BEM or CFD result is claimed.</p>
            <code>{snapshot.canary.archiveMember}</code>
            <code data-testid="propulsor-source-sha">{snapshot.canary.sourceSha256}</code>
            <code data-testid="propulsor-fixture-revision">{PROPULSOR_DEMO_EVIDENCE.fixtureRevision}</code>
          </aside>
        </details>
      </div>
    </section>
  </article>;
}

export default PropulsorDemo;
